import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence, useMotionValueEvent } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Eyebrow } from "@/components/ui-custom/Eyebrow";
import { Reveal } from "@/components/ui-custom/Reveal";
import { StatCounter } from "@/components/ui-custom/StatCounter";
import { TrustBadges } from "@/components/ui-custom/TrustBadges";
import { Marquee } from "@/components/ui-custom/Marquee";
import { PreFooterCTA } from "@/components/sections/PreFooterCTA";
import { StackingServices } from "@/components/sections/StackingServices";
import { TestimonialsWall } from "@/components/sections/TestimonialsWall";
import {
  HOME_STATS,
  CLIENT_CONCERNS,
  TRUST_BADGES,
  AIM_STANDARD,
} from "@/data/site";
import { RESIDENTIAL_SERVICES, COMMERCIAL_SERVICES } from "@/data/services";
import { HOME_HERO_SLIDES as HERO_SLIDES, HOME_ABOUT_IMAGE } from "@/data/media";
import { useSEO, localBusinessSchema } from "@/lib/seo";

const H1_WORDS =
  "Full-Service Exterior & General Contractor in Monmouth County, NJ".split(" ");

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = () => {
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative overflow-hidden" style={{ minHeight: "92vh", background: "var(--black)", marginTop: "-72px" }} data-testid="home-hero">
      {/* slider backgrounds */}
      <AnimatePresence>
        <motion.div
          key={slide}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.2 }, scale: { duration: 6, ease: "linear" } }}
          data-testid={`hero-slide-${slide}`}
        >
          <div className="absolute inset-0" style={{ background: "var(--charcoal)" }} />
          <img
            src={HERO_SLIDES[slide].src}
            alt={HERO_SLIDES[slide].label}
            className="absolute inset-0"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </motion.div>
      </AnimatePresence>
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(9,9,11,0.82) 0%, rgba(9,9,11,0.5) 46%, rgba(9,9,11,0.16) 100%)" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(9,9,11,0.55) 0%, rgba(9,9,11,0) 42%)" }} />

      <div className="container-1200 relative z-10 flex flex-col justify-center" style={{ minHeight: "92vh", paddingTop: "96px", paddingBottom: "96px" }}>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Eyebrow start data-testid="hero-eyebrow">Licensed &amp; Insured · Colts Neck, NJ</Eyebrow>
        </motion.div>

        <h1 className="h1 mt-7" style={{ color: "var(--text-light)", maxWidth: "20ch", lineHeight: 1.08 }} data-testid="hero-h1">
          {H1_WORDS.map((w, i) => (
            <motion.span
              key={i}
              style={{ display: "inline-block", marginRight: "0.26em" }}
              initial={{ y: 26, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.65, delay: 0.15 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
            >
              {w}
            </motion.span>
          ))}
        </h1>

        <motion.p className="lead mt-7" style={{ color: "rgba(245,242,236,0.82)", maxWidth: "48ch" }} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }} data-testid="hero-subline">
          Honest pricing. No surprises. 20+ years of craftsmanship.
        </motion.p>

        <motion.div className="flex flex-col sm:flex-row gap-4 mt-10" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }}>
          <button className="btn btn-primary" onClick={() => scrollToId("residential")} data-testid="hero-residential-btn">Residential Services</button>
          <button className="btn btn-secondary" onClick={() => scrollToId("commercial")} data-testid="hero-commercial-btn">Commercial Services</button>
        </motion.div>

        {/* slide dots */}
        <div className="flex gap-2.5 mt-12" data-testid="hero-dots">
          {HERO_SLIDES.map((s, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              aria-label={s.label}
              data-testid={`hero-dot-${i}`}
              style={{ width: i === slide ? 30 : 10, height: 5, borderRadius: 999, background: i === slide ? "var(--cta)" : "rgba(245,242,236,0.28)", transition: "all 0.4s ease" }}
            />
          ))}
        </div>
      </div>

      <motion.div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10" initial={{ opacity: 0 }} animate={{ opacity: 1, y: [0, 8, 0] }} transition={{ opacity: { delay: 1.2 }, y: { repeat: Infinity, duration: 2 } }}>
        <ArrowDown size={22} strokeWidth={1.5} style={{ color: "var(--cta)" }} />
      </motion.div>
    </section>
  );
};

const WeGetItTimeline = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [active, setActive] = useState(0);
  const total = CLIENT_CONCERNS.length;

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const idx = Math.min(total - 1, Math.max(0, Math.floor(v * total)));
    setActive(idx);
  });

  const c = CLIENT_CONCERNS[active];

  return (
    <section ref={ref} className="sec-gray" style={{ height: `${total * 90}vh` }} data-testid="home-concerns">
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        <div className="container-1200 w-full">
          <Eyebrow start>We Get It</Eyebrow>
          <h2 className="h2 mt-5" style={{ maxWidth: "22ch" }}>Hiring a Contractor Shouldn't Feel Like a Gamble</h2>

          {/* progress segments */}
          <div className="flex gap-3 mt-9" data-testid="concern-progress">
            {CLIENT_CONCERNS.map((_, i) => (
              <div key={i} style={{ height: 4, flex: 1, borderRadius: 999, background: "rgba(23,23,26,0.12)", overflow: "hidden" }}>
                <motion.div
                  initial={false}
                  animate={{ width: i <= active ? "100%" : "0%" }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  style={{ height: "100%", background: "var(--cta-gradient)" }}
                />
              </div>
            ))}
          </div>

          <div className="relative mt-12" style={{ minHeight: 260 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`concern-${active}`}
              >
                <div className="flex items-center gap-4">
                  <span className="gold-numeral" style={{ fontSize: 48, lineHeight: 1 }}>{`0${active + 1} / 0${total}`}</span>
                </div>
                <p className="text-muted-warm mt-6" style={{ fontSize: 20, fontStyle: "italic", maxWidth: "40ch" }}>“{c.worry}”</p>
                <p className="mt-4 h2" style={{ fontSize: "clamp(24px,3.4vw,38px)", textTransform: "none", letterSpacing: 0, maxWidth: "24ch", color: "var(--text-dark)" }}>{c.answer}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          <p className="text-muted-warm mt-10 hidden md:block" style={{ fontSize: 13, letterSpacing: "0.14em", textTransform: "uppercase" }}>Keep scrolling ↓</p>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  useSEO({
    title: "AIM High | Full-Service Exterior & General Contractor | Monmouth County NJ",
    description:
      "AIM High — licensed & insured full-service exterior & general contractor in Colts Neck, NJ. Roofing, siding, gutters, renovations & commercial build-outs across Monmouth County. 20+ years. Free estimates.",
    path: "/",
    jsonLd: [localBusinessSchema()],
  });

  return (
    <>
      <Hero />

      {/* Stats bar */}
      <section className="sec-black" style={{ borderTop: "1px solid var(--border-dark)", borderBottom: "1px solid var(--border-dark)" }} data-testid="home-stats">
        <div className="container-1200 py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            {HOME_STATS.map((s) => (
              <div key={s.label} className="stat-jump cursor-default">
                <StatCounter value={s.value} suffix={s.suffix} label={s.label} data-testid={`stat-${s.value}${s.suffix}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Residential services — stacking cards */}
      <StackingServices id="residential" data-testid="home-residential" eyebrow="Residential" heading="Exterior & Home Renovation Services" description="Whatever your home needs — inside or out — you get one experienced crew, an honest price up front, and work that stays on schedule." services={RESIDENTIAL_SERVICES} dark={false} />

      {/* Commercial services — stacking cards */}
      <StackingServices id="commercial" data-testid="home-commercial" eyebrow="Commercial" heading="Commercial Construction & Maintenance" description="From build-outs to ongoing upkeep, keep your business open and running with a licensed commercial crew that shows up and delivers on time." services={COMMERCIAL_SERVICES} dark={true} footNote="Registered with New York City for property maintenance." />

      {/* About / brand story — sticky image, scrolling text */}
      <section className="sec-white section-y" data-testid="home-about">
        <div className="container-1200 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div className="lg:sticky lg:top-[104px]">
            <Reveal>
              <div className="overflow-hidden" style={{ borderRadius: 10 }}>
                <img src={HOME_ABOUT_IMAGE} alt="A finished AIM High home in Monmouth County, NJ" loading="lazy" style={{ width: "100%", display: "block", aspectRatio: "4 / 3", objectFit: "cover" }} />
              </div>
              <div className="divider-gold my-7" />
              <TrustBadges items={TRUST_BADGES} />
            </Reveal>
          </div>

          <div>
            <Reveal>
              <Eyebrow start>Our Story</Eyebrow>
              <h2 className="h2 mt-5" style={{ maxWidth: "16ch" }}>A Family Name. A Craftsman's Standard.</h2>
              <div className="mt-6 space-y-4" style={{ fontSize: 17, lineHeight: 1.7, color: "var(--text-dark)" }}>
                <p>The name "AIM" comes from the initials of the owner's children — this is a family business, built on fairness and honesty. That's not a slogan; it's how every quote is written and every job is run.</p>
                <p>Behind the work is a former union carpenter and millwright with 20+ years in construction and 14 years running this company. You get that experience on every project — plus the peace of mind that you're covered: licensed, insured, and NJ HIC registered.</p>
              </div>
            </Reveal>

            {/* The AIM Standard — ledger */}
            <div className="mt-12">
              <Reveal>
                <div className="eyebrow eyebrow-start mb-2">The AIM Standard</div>
              </Reveal>
              {AIM_STANDARD.map((s, i) => (
                <Reveal key={s.k} delay={i * 0.06}>
                  <div className="grid grid-cols-[auto_1fr] gap-6 items-start py-7" style={{ borderTop: "1px solid #e2ddd1" }}>
                    <span className="gold-numeral" style={{ fontSize: 40, lineHeight: 1 }}>{s.k}</span>
                    <div>
                      <h3 className="h3" style={{ fontSize: 22, color: "var(--text-dark)" }}>{s.title}</h3>
                      <p className="mt-2 text-muted-warm" style={{ fontSize: 16, lineHeight: 1.6 }}>{s.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* We Get It — timeline */}
      <WeGetItTimeline />

      {/* Testimonials wall */}
      <TestimonialsWall />

      {/* Service area strip */}
      <section className="sec-black" data-testid="home-area-strip" style={{ borderTop: "1px solid var(--border-dark)" }}>
        <div className="py-8">
          <Marquee items={["Monmouth County", "Union County", "Middlesex County", "Ocean County", "NYC Property Maintenance", "Licensed & Insured", "20+ Years"]} data-testid="area-marquee" />
        </div>
        <div className="container-1200 pb-10 text-center">
          <Link to="/service-areas" className="inline-flex items-center gap-2 text-gold" data-testid="area-strip-link" style={{ fontWeight: 600, color: "var(--cta)" }}>
            See all the areas we serve
            <ArrowRight size={17} strokeWidth={1.75} />
          </Link>
        </div>
      </section>

      <PreFooterCTA />
    </>
  );
}
